import React, { useState } from 'react';
import { TrustedAdvisor, AdvisorPermissionsSummary } from '../../types/trustedAdvisor';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { 
  Users, 
  Mail, 
  Phone, 
  MoreVertical, 
  Edit, 
  Trash2, 
  RefreshCw,
  Shield,
  CheckCircle,
  Clock,
  X,
  Eye,
  FileText
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface TrustedAdvisorCardProps {
  advisor: TrustedAdvisor;
  permissions_summary?: AdvisorPermissionsSummary;
  onEdit: (advisor: TrustedAdvisor) => void;
  onRemove: (advisor_id: string) => void;
  onResendInvitation: (advisor_id: string) => void;
  onManagePermissions: (advisor_id: string) => void;
}

const TrustedAdvisorCard: React.FC<TrustedAdvisorCardProps> = ({
  advisor,
  permissions_summary,
  onEdit,
  onRemove,
  onResendInvitation,
  onManagePermissions
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'removed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'removed':
        return <X className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getPermissionTypeColor = (type: string) => {
    switch (type) {
      case 'share_now':
        return 'bg-blue-100 text-blue-800';
      case 'share_after_death':
        return 'bg-purple-100 text-purple-800';
      case 'do_not_share':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPermissionTypeLabel = (type: string) => {
    switch (type) {
      case 'share_now':
        return 'Share Now';
      case 'share_after_death':
        return 'Share After Death';
      case 'do_not_share':
        return 'Do Not Share';
      default:
        return type;
    }
  };

  return (
    <Card className="p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        {/* Advisor Info */}
        <div className="flex items-start space-x-4">
          {/* Avatar */}
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-blue-600 font-semibold text-lg">
              {advisor.first_name[0]}{advisor.last_name[0]}
            </span>
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {advisor.first_name} {advisor.last_name}
              </h3>
              <Badge className={getStatusColor(advisor.status)}>
                <div className="flex items-center space-x-1">
                  {getStatusIcon(advisor.status)}
                  <span className="capitalize">{advisor.status}</span>
                </div>
              </Badge>
            </div>
            
            <p className="text-sm text-gray-600 mb-2">{advisor.relationship}</p>
            
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-1" />
                <span className="truncate">{advisor.email}</span>
              </div>
              
              {advisor.status === 'pending' && (
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>Invited {formatDistanceToNow(new Date(advisor.invited_at), { addSuffix: true })}</span>
                </div>
              )}
              
              {advisor.status === 'active' && advisor.activated_at && (
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  <span>Active since {formatDistanceToNow(new Date(advisor.activated_at), { addSuffix: true })}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions Menu */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowMenu(!showMenu)}
            className="p-2"
          >
            <MoreVertical className="w-4 h-4" />
          </Button>

          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
              <div className="py-1">
                <button
                  onClick={() => {
                    onEdit(advisor);
                    setShowMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Advisor
                </button>
                
                <button
                  onClick={() => {
                    onManagePermissions(advisor.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Manage Permissions
                </button>
                
                {advisor.status === 'pending' && (
                  <button
                    onClick={() => {
                      onResendInvitation(advisor.id);
                      setShowMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Resend Invitation
                  </button>
                )}
                
                {advisor.status !== 'removed' && (
                  <button
                    onClick={() => {
                      onRemove(advisor.id);
                      setShowMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Advisor
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Permissions Summary */}
      {permissions_summary && permissions_summary.total_permissions > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Document Permissions
            </h4>
            <Badge variant="outline">
              {permissions_summary.total_permissions} documents
            </Badge>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            {permissions_summary.share_now_count > 0 && (
              <div className="text-center p-2 bg-blue-50 rounded">
                <Badge className={getPermissionTypeColor('share_now')}>
                  {permissions_summary.share_now_count}
                </Badge>
                <p className="text-xs text-gray-600 mt-1">Share Now</p>
              </div>
            )}
            
            {permissions_summary.share_after_death_count > 0 && (
              <div className="text-center p-2 bg-purple-50 rounded">
                <Badge className={getPermissionTypeColor('share_after_death')}>
                  {permissions_summary.share_after_death_count}
                </Badge>
                <p className="text-xs text-gray-600 mt-1">After Death</p>
              </div>
            )}
            
            {permissions_summary.do_not_share_count > 0 && (
              <div className="text-center p-2 bg-gray-50 rounded">
                <Badge className={getPermissionTypeColor('do_not_share')}>
                  {permissions_summary.do_not_share_count}
                </Badge>
                <p className="text-xs text-gray-600 mt-1">Do Not Share</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(advisor)}
            className="flex-1"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManagePermissions(advisor.id)}
            className="flex-1"
          >
            <Shield className="w-4 h-4 mr-2" />
            Permissions
          </Button>
          
          {advisor.status === 'pending' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResendInvitation(advisor.id)}
              className="flex-1"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Resend
            </Button>
          )}
        </div>
      </div>

      {/* Status-specific information */}
      {advisor.status === 'pending' && (
        <div className="mt-3 p-3 bg-yellow-50 rounded-md">
          <div className="flex items-center">
            <Clock className="w-4 h-4 text-yellow-600 mr-2" />
            <p className="text-sm text-yellow-800">
              Invitation sent. Waiting for {advisor.first_name} to accept.
            </p>
          </div>
        </div>
      )}

      {advisor.status === 'removed' && (
        <div className="mt-3 p-3 bg-red-50 rounded-md">
          <div className="flex items-center">
            <X className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-sm text-red-800">
              This trusted advisor has been removed and no longer has access.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TrustedAdvisorCard; 